import { RoutesService, eLayoutType, PermissionService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { ConfigStateService } from '@abp/ng.core';

/**
 * Menu contributor that follows ABP standards for centralized menu definition
 * This makes the menu structure maintainable and properly integrated with ABP's permission system
 */
@Injectable({
  providedIn: 'root'
})
export class AppMenuContributor {
  constructor(
    private configState: ConfigStateService,
    private permissionService: PermissionService,
    private routesService: RoutesService
  ) {}

  buildMenu(): void {
    this.configureMainMenu();
  }

  private configureMainMenu(): void {
    // Dashboard (role-based: user dashboard, PM dashboard, etc.)
    this.addMenuItem({
      path: '/',
      name: '::Dashboard',
      order: 1,
      iconClass: 'prose-mi prose-mi-dashboard',
      layout: eLayoutType.application
    });

    // Project Management
    this.addMenuItem({
      path: '/section/project-management',
      name: '::Project Management',
      order: 2,
      iconClass: 'prose-mi prose-mi-folder',
      layout: eLayoutType.application
    });

    // Process Design
    this.addMenuItem({
      path: '/section/process-design',
      name: '::Process Design',
      order: 3,
      iconClass: 'prose-mi prose-mi-data',
      layout: eLayoutType.application
    });

    // Technical Design
    this.addMenuItem({
      path: '/section/technical-design',
      name: '::Technical Design',
      order: 4,
      iconClass: 'prose-mi prose-mi-data',
      layout: eLayoutType.application
    });

    // Testing
    this.addMenuItem({
      path: '/section/testing',
      name: '::Testing',
      order: 5,
      iconClass: 'prose-mi prose-mi-monitor',
      layout: eLayoutType.application
    });

    // Reports
    this.addMenuItem({
      path: '/dynamicReport',
      name: '::Reports',
      order: 6,
      iconClass: 'prose-mi prose-mi-info',
      layout: eLayoutType.application,
      requiredPolicy: 'ReportModule.Report.View || ReportModule.Report.Export'
    });

    // Configuration Group
    this.configureConfigurationMenu();

    // Administration Group
    this.configureAdministrationMenu();

    // Remove ABP module-default menu items that the previous (custom) sidebar
    // never showed — keeps the menu matching the prior look.
    this.removeUnwantedDefaultMenuItems();
  }

  /**
   * Remove ABP's default Administration menu group and its children
   * (Identity Management, Tenant Management, Settings). The app exposes its
   * own Administration group (Users/Roles) and does not surface tenant
   * management or settings in the sidebar. RoutesService.remove() cascades to
   * children of any named parent.
   */
  private removeUnwantedDefaultMenuItems(): void {
    this.routesService.remove([
      'AbpUiNavigation::Menu:Administration',
      'AbpIdentity::Menu:IdentityManagement',
      'AbpTenantManagement::Menu:TenantManagement',
      'AbpSettingManagement::Settings',
    ]);
  }

  private configureConfigurationMenu(): void {
    // Permission-gated via requiredPolicy. NOTE: the backend permission group is
    // "ConfigurationModule" (see ProsePermissions.cs / configuration.component.ts),
    // NOT "ProsePermissions" — using the wrong prefix made this whole group
    // invisible even for admins who hold the permission. ABP's isPolicyGranted
    // parses the `||` expression and the menu hides items reactively.
    const configurationPolicy =
      'ConfigurationModule.Configuration.Create || ConfigurationModule.Configuration.Edit || ConfigurationModule.Configuration.Delete';

    this.addMenuItem({
      name: '::Configuration',
      order: 90,
      iconClass: 'prose-mi prose-mi-setting',
      layout: eLayoutType.application,
      requiredPolicy: configurationPolicy
    });

    // Form Configuration
    this.addMenuItem({
      path: '/configuration',
      name: '::Form Configuration',
      parentName: '::Configuration',
      order: 1,
      iconClass: 'prose-mi prose-mi-setting',
      layout: eLayoutType.application,
      requiredPolicy: configurationPolicy
    });

    // List Configuration
    this.addMenuItem({
      path: '/dropdownConfig',
      name: '::List Configuration',
      parentName: '::Configuration',
      order: 2,
      iconClass: 'prose-mi prose-mi-setting',
      layout: eLayoutType.application,
      requiredPolicy: configurationPolicy
    });
  }

  private configureAdministrationMenu(): void {
    // Always register with requiredPolicy; ABP hides items reactively for users
    // without permission (see note in configureConfigurationMenu about timing).
    this.addMenuItem({
      name: '::Administration',
      order: 100,
      iconClass: 'prose-mi prose-mi-setting',
      layout: eLayoutType.application,
      requiredPolicy: 'AbpIdentity.Users || AbpIdentity.Roles'
    });

    // Users menu item
    this.addMenuItem({
      path: '/identity/users',
      name: '::Users',
      parentName: '::Administration',
      iconClass: 'prose-mi prose-mi-users',
      order: 1,
      requiredPolicy: 'AbpIdentity.Users',
      layout: eLayoutType.application
    });

    // Roles menu item
    this.addMenuItem({
      path: '/identity/roles',
      name: '::Roles',
      parentName: '::Administration',
      iconClass: 'prose-mi prose-mi-users',
      order: 2,
      requiredPolicy: 'AbpIdentity.Roles',
      layout: eLayoutType.application
    });
  }

  // Helper method to add menu items using the RoutesService
  private addMenuItem(menuItem: {
    name: string;
    parentName?: string;
    order?: number;
    path?: string;
    iconClass?: string;
    requiredPolicy?: string;
    layout: eLayoutType;
  }): void {
    this.routesService.add([{
      path: menuItem.path || '',
      name: menuItem.name,
      parentName: menuItem.parentName,
      order: menuItem.order || 999,
      iconClass: menuItem.iconClass,
      requiredPolicy: menuItem.requiredPolicy,
      layout: menuItem.layout
    }]);
  }
}